import { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tag, MagnifyingGlass, UploadSimple, DownloadSimple, ChartBar, FloppyDisk, Check, X,
} from "@phosphor-icons/react";

const TABS = [
  { key: "list",   label: "Master Price List", icon: Tag },
  { key: "report", label: "Purchase vs Consumption", icon: ChartBar },
];

export default function PricesPage() {
  const { user } = useAuth();
  const canEdit = user.role === "finance" || user.role === "manager";
  const [tab, setTab] = useState("list");

  return (
    <div data-testid="prices-page">
      <div className="border-b border-neutral-200 px-10 py-8 bg-white sticky top-0 z-10">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="label-eyebrow">Finance</div>
            <h1 className="font-display text-4xl tracking-tight mt-1">Master Price List</h1>
            <div className="text-xs text-neutral-500 mt-1">Confidential — visible only to Finance and Managers.</div>
          </div>
        </div>

        <div className="mt-6 flex border border-neutral-300" data-testid="prices-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`h-11 px-4 text-xs font-mono uppercase tracking-wider border-r last:border-r-0 border-neutral-300 flex-1 inline-flex items-center justify-center gap-2 ${
                  active ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"
                }`}
              >
                <Icon size={13} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-10">
        {tab === "list" && <PriceListTab canEdit={canEdit} />}
        {tab === "report" && <ReportTab />}
      </div>
    </div>
  );
}


function PriceListTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/stock?limit=10000");
      setItems(res.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((r) =>
      (r.item || "").toLowerCase().includes(t)
      || (r.description || "").toLowerCase().includes(t)
      || (r.group || "").toLowerCase().includes(t),
    );
  }, [items, q]);

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditValue(row.unit_cost != null ? String(row.unit_cost) : "");
  };
  const cancelEdit = () => { setEditingId(null); setEditValue(""); };
  const saveEdit = async (row) => {
    const price = parseFloat(editValue);
    if (isNaN(price) || price < 0) return toast.error("Enter a valid non-negative price");
    try {
      const res = await api.patch(`/stock/${row.id}/unit-cost`, { unit_cost: price });
      toast.success(`${row.item}: ${res.data.before.toFixed(2)} → ${res.data.after.toFixed(2)}`);
      setItems((prev) => prev.map((it) => it.id === row.id ? { ...it, unit_cost: res.data.after } : it));
      cancelEdit();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const priced = items.filter((r) => Number(r.unit_cost) > 0).length;
  const unpriced = items.length - priced;

  const exportTemplate = () => {
    const header = "part_number,new_unit_price\n";
    const rows = items.slice(0, 20).map((r) => `${csvSafe(r.item)},${r.unit_cost || 0}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "price-list-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="price-list-tab">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Master items" value={items.length.toLocaleString()} />
        <StatCard label="Priced" value={priced.toLocaleString()} tone="emerald" />
        <StatCard label="Missing price" value={unpriced.toLocaleString()} tone={unpriced ? "amber" : "neutral"} />
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Filter by part number, description, group…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-none border-neutral-400"
            data-testid="price-filter"
          />
        </div>
        <Button
          onClick={exportTemplate}
          variant="outline"
          className="h-10 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white"
          data-testid="download-template"
        >
          <DownloadSimple size={14} weight="bold" className="mr-1" /> Template CSV
        </Button>
        {canEdit && (
          <Button
            onClick={() => setBulkOpen(true)}
            className="h-10 rounded-none bg-neutral-900 text-white hover:bg-neutral-800"
            data-testid="bulk-upload-btn"
          >
            <UploadSimple size={14} weight="bold" className="mr-1" /> Bulk Price Upload
          </Button>
        )}
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="prices-table">
          <thead className="border-b border-neutral-200 bg-neutral-50 sticky top-0">
            <tr className="text-left">
              <th className="px-3 py-2 label-eyebrow">Part #</th>
              <th className="px-3 py-2 label-eyebrow">Description</th>
              <th className="px-3 py-2 label-eyebrow">Group</th>
              <th className="px-3 py-2 label-eyebrow">UOM</th>
              <th className="px-3 py-2 label-eyebrow text-right">Unit Cost (MYR)</th>
              {canEdit && <th className="px-3 py-2 label-eyebrow w-24"></th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-10 text-center text-neutral-400 text-xs">Loading {items.length ? "" : "master list…"}</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-neutral-400">No parts match.</td></tr>
            )}
            {!loading && filtered.slice(0, 500).map((row) => {
              const editing = editingId === row.id;
              return (
                <tr key={row.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`price-row-${row.id}`}>
                  <td className="px-3 py-1.5 font-mono text-xs">{row.item}</td>
                  <td className="px-3 py-1.5">{row.description}</td>
                  <td className="px-3 py-1.5 text-xs text-neutral-500">{row.group}</td>
                  <td className="px-3 py-1.5 text-xs">{row.uom}</td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    {editing ? (
                      <Input
                        type="number" step="0.01" min={0}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit(row);
                          else if (e.key === "Escape") cancelEdit();
                        }}
                        autoFocus
                        className="h-7 w-24 ml-auto rounded-none border-neutral-900 font-mono text-right"
                        data-testid={`price-input-${row.id}`}
                      />
                    ) : (
                      Number(row.unit_cost) > 0
                        ? <span>RM {Number(row.unit_cost).toFixed(2)}</span>
                        : <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-1.5">
                      {editing ? (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => saveEdit(row)} className="p-1 border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`price-save-${row.id}`}><Check size={12} weight="bold" /></button>
                          <button onClick={cancelEdit} className="p-1 border border-neutral-300 hover:border-red-600 hover:text-red-600"><X size={12} weight="bold" /></button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(row)}
                          className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-neutral-300 hover:bg-neutral-900 hover:text-white"
                          data-testid={`price-edit-${row.id}`}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length > 500 && (
          <div className="px-3 py-2 border-t border-neutral-200 text-[11px] text-neutral-500 font-mono">
            Showing first 500 of {filtered.length.toLocaleString()} — refine the filter to narrow the list.
          </div>
        )}
      </div>

      <BulkUploadDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={load} />
    </div>
  );
}


function BulkUploadDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setFile(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; }
  }, [open]);

  const submit = async () => {
    if (!file) return toast.error("Pick a CSV or XLSX file first");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/stock/bulk-price-upload", form);
      setResult(res.data);
      toast.success(`Updated ${res.data.updated_count} price${res.data.updated_count === 1 ? "" : "s"}`);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-none border-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Bulk Price Upload</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
            Upload a <b>CSV</b> or <b>XLSX</b> with two columns:{" "}
            <span className="font-mono">Part Number</span> and{" "}
            <span className="font-mono">New Unit Price</span>. Column names are matched case-insensitively — synonyms like <span className="font-mono">SKU / Item / Code</span> and <span className="font-mono">Unit Cost / Price</span> also work.
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">File</Label>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm file:mr-3 file:py-2 file:px-3 file:border file:border-neutral-900 file:bg-neutral-900 file:text-white file:font-mono file:text-xs file:uppercase file:cursor-pointer hover:file:bg-neutral-800"
              data-testid="bulk-file-input"
            />
          </div>

          {result && (
            <div className="border border-neutral-300 bg-white" data-testid="bulk-result">
              <div className="grid grid-cols-4 divide-x divide-neutral-200 text-center">
                <ResultCell label="Updated" value={result.updated_count} tone="emerald" />
                <ResultCell label="Unchanged" value={result.unchanged_count} tone="neutral" />
                <ResultCell label="Unmatched" value={result.unmatched_count} tone={result.unmatched_count ? "amber" : "neutral"} />
                <ResultCell label="Errors" value={result.error_count} tone={result.error_count ? "red" : "neutral"} />
              </div>
              <div className="border-t border-neutral-200 px-3 py-2 text-[11px] font-mono text-neutral-500">
                Processed {result.total_rows} data row{result.total_rows === 1 ? "" : "s"}
              </div>

              {result.updated?.length > 0 && (
                <details className="border-t border-neutral-200">
                  <summary className="px-3 py-2 text-xs font-mono uppercase tracking-wider cursor-pointer hover:bg-neutral-50">Updated ({result.updated.length})</summary>
                  <div className="max-h-40 overflow-y-auto border-t border-neutral-200">
                    <table className="w-full text-xs">
                      <tbody>
                        {result.updated.map((r) => (
                          <tr key={`u:${r.part_number}`} className="border-b border-neutral-100 last:border-0">
                            <td className="px-3 py-1 font-mono">{r.part_number}</td>
                            <td className="px-3 py-1 text-right text-neutral-500 font-mono">{r.before.toFixed(2)} → <b className="text-emerald-700">{r.after.toFixed(2)}</b></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
              {result.unmatched?.length > 0 && (
                <details className="border-t border-neutral-200">
                  <summary className="px-3 py-2 text-xs font-mono uppercase tracking-wider cursor-pointer hover:bg-neutral-50 text-amber-800">Unmatched — not in master list ({result.unmatched.length})</summary>
                  <div className="max-h-40 overflow-y-auto border-t border-neutral-200">
                    <table className="w-full text-xs">
                      <tbody>
                        {result.unmatched.map((r) => (
                          <tr key={`m:${r.row}:${r.part_number}`} className="border-b border-neutral-100 last:border-0">
                            <td className="px-3 py-1 font-mono text-neutral-400 w-16">row {r.row}</td>
                            <td className="px-3 py-1 font-mono">{r.part_number}</td>
                            <td className="px-3 py-1 text-right font-mono">{Number(r.new_price).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
              {result.errors?.length > 0 && (
                <details className="border-t border-neutral-200">
                  <summary className="px-3 py-2 text-xs font-mono uppercase tracking-wider cursor-pointer hover:bg-neutral-50 text-red-800">Errors ({result.errors.length})</summary>
                  <div className="max-h-40 overflow-y-auto border-t border-neutral-200">
                    <table className="w-full text-xs">
                      <tbody>
                        {result.errors.map((r) => (
                          <tr key={`e:${r.row}`} className="border-b border-neutral-100 last:border-0">
                            <td className="px-3 py-1 font-mono text-neutral-400 w-16">row {r.row}</td>
                            <td className="px-3 py-1 font-mono">{r.part_number || "—"}</td>
                            <td className="px-3 py-1 text-red-700">{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">
            {result ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !file}
            data-testid="bulk-submit"
            className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1" />
            {busy ? "Uploading…" : (result ? "Upload another" : "Upload prices")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReportTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/purchase-vs-consumption`, { params: { year, month } });
      setReport(res.data);
    } catch (e) { toast.error(formatApiError(e)); setReport(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year, month]);

  const totals = report?.totals || {};
  const rows = report?.rows || [];

  const exportCsv = () => {
    const header = "stock_code,description,group,uom,current_unit_cost,purchased_qty,purchased_value,consumed_qty,consumed_value,net_delta_qty\n";
    const body = rows.map((r) =>
      [r.stock_code, r.description, r.group, r.uom, r.current_unit_cost, r.purchased_qty, r.purchased_value, r.consumed_qty, r.consumed_value, r.net_delta_qty]
        .map(csvSafe).join(",")
    ).join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `purchase-vs-consumption-${year}-${String(month).padStart(2,"0")}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="report-tab">
      <div className="flex items-center gap-3">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Year</Label>
          <Input type="number" min={2020} max={2100} value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-10 w-28 rounded-none border-neutral-400 font-mono" data-testid="report-year" />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Month</Label>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="h-10 border border-neutral-400 rounded-none px-3 bg-white text-sm" data-testid="report-month">
            {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div className="flex-1" />
        <Button
          onClick={exportCsv}
          disabled={!rows.length}
          variant="outline"
          className="h-10 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white disabled:opacity-40"
          data-testid="report-export"
        >
          <DownloadSimple size={14} weight="bold" className="mr-1" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Purchased qty" value={(totals.purchased_qty || 0).toLocaleString()} />
        <StatCard label="Purchased value (MYR)" value={fmtMoney(totals.purchased_value)} mono />
        <StatCard label="Consumed qty" value={(totals.consumed_qty || 0).toLocaleString()} />
        <StatCard label="Consumed value (MYR)" value={fmtMoney(totals.consumed_value)} mono />
      </div>

      <div className={`border px-3 py-2 font-mono flex items-center justify-between ${Number(totals.net_value) >= 0 ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-red-600 bg-red-50 text-red-900"}`}>
        <span className="uppercase tracking-wider text-[10px] opacity-80">Net (purchased − consumed)</span>
        <span className="text-lg">{fmtMoney(totals.net_value)}</span>
      </div>

      {/* Consumption By Region — where the spend actually landed */}
      {Array.isArray(report?.by_region) && report.by_region.length > 0 && (
        <div className="border border-neutral-900 bg-white" data-testid="consumption-by-region">
          <div className="border-b border-neutral-200 px-3 py-2 bg-neutral-50 flex items-center justify-between">
            <div className="label-eyebrow">Consumption By Region</div>
            <div className="text-[10px] font-mono text-neutral-500">
              {report.by_region.length} region{report.by_region.length === 1 ? "" : "s"} · {fmtMoney(totals.consumed_value)} total
            </div>
          </div>
          <div className="p-4 space-y-2">
            {report.by_region.map((r) => (
              <div key={r.region} className="flex items-center gap-3" data-testid={`region-row-${r.region}`}>
                <div className="w-32 text-sm font-medium truncate">{r.region}</div>
                <div className="flex-1 relative h-6 bg-neutral-100 border border-neutral-200">
                  <div
                    className="absolute inset-y-0 left-0 bg-neutral-900"
                    style={{ width: `${Math.min(100, r.share_pct)}%` }}
                  />
                  <div className="relative z-10 px-2 h-full flex items-center text-[11px] font-mono text-neutral-900 mix-blend-difference">
                    {r.share_pct.toFixed(1)}%
                  </div>
                </div>
                <div className="w-24 text-right font-mono text-sm">{r.consumed_qty} units</div>
                <div className="w-32 text-right font-mono text-sm">{fmtMoney(r.consumed_value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-table">
          <thead className="border-b border-neutral-200 bg-neutral-50">
            <tr className="text-left">
              <th className="px-3 py-2 label-eyebrow">Part #</th>
              <th className="px-3 py-2 label-eyebrow">Description</th>
              <th className="px-3 py-2 label-eyebrow text-right">Unit cost (MYR)</th>
              <th className="px-3 py-2 label-eyebrow text-right">Pur qty</th>
              <th className="px-3 py-2 label-eyebrow text-right">Pur value (MYR)</th>
              <th className="px-3 py-2 label-eyebrow text-right">Cons qty</th>
              <th className="px-3 py-2 label-eyebrow text-right">Cons value (MYR)</th>
              <th className="px-3 py-2 label-eyebrow text-right">Δ qty</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-10 text-center text-neutral-400 text-xs">Computing…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-neutral-400">No purchases or consumption recorded for this month.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.stock_code} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                <td className="px-3 py-1.5 font-mono text-xs">{r.stock_code}</td>
                <td className="px-3 py-1.5">{r.description}</td>
                <td className="px-3 py-1.5 text-right font-mono">RM {Number(r.current_unit_cost).toFixed(2)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{r.purchased_qty}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(r.purchased_value)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{r.consumed_qty}</td>
                <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(r.consumed_value)}</td>
                <td className={`px-3 py-1.5 text-right font-mono ${r.net_delta_qty < 0 ? "text-red-700" : ""}`}>{r.net_delta_qty > 0 ? "+" : ""}{r.net_delta_qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function StatCard({ label, value, tone = "neutral", mono }) {
  const tones = {
    neutral: "border-neutral-200",
    emerald: "border-emerald-500 bg-emerald-50",
    amber: "border-amber-500 bg-amber-50",
    red: "border-red-500 bg-red-50",
  };
  return (
    <div className={`border ${tones[tone]} bg-white px-4 py-3`}>
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1 font-display text-2xl ${mono ? "font-mono text-xl" : ""}`}>{value}</div>
    </div>
  );
}

function ResultCell({ label, value, tone }) {
  const tones = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-red-700",
    neutral: "text-neutral-700",
  };
  return (
    <div className="py-3">
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1 font-display text-2xl font-mono ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function fmtMoney(v) {
  if (v == null || isNaN(v)) return "RM 0.00";
  return `RM ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function csvSafe(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s) || /^[=+\-@\t\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
